import {
  ApiResponse,
  SurveyData,
  ApiFilterOptions,
  FeedbackDashboardMetrics,
  PartnerDashboardMetrics,
  DepartmentDashboardMetrics,
  MEDashboardMetrics,
  PublicMEDashboardMetrics,
} from '../types';

// ============================================================================
// SINGLE SOURCE OF TRUTH: API Configuration
// ============================================================================
// Change PRODUCTION_DOMAIN here to update the production host globally
export const PRODUCTION_DOMAIN = 'common.karven.in';

/**
 * Get the base API URL based on current location
 * Returns base URL without /api suffix (e.g., 'http://localhost:3001' or 'https://msmeramp.staging.ap.gov.in')
 */
export const getApiBaseUrl = (): string => {
  // Check if running on production domain
  if (window.location.hostname === PRODUCTION_DOMAIN) {
    // Use the same protocol (http or https) as the current page
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
    return `${protocol}://${PRODUCTION_DOMAIN}`;
  }
  
  // For localhost development
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    const port = window.location.port;
    
    // If accessing via React dev server (port 3000), connect directly to backend on port 3001
    if (port === '3000') {
      return 'http://localhost:3001';
    }
    
    // Default for other localhost ports - use same origin
    return `${window.location.protocol}//${window.location.hostname}${port ? `:${port}` : ''}`;
  }
  
  // For ramp.localhost (Caddy reverse proxy), use same origin
  if (window.location.hostname === 'ramp.localhost') {
    return `${window.location.protocol}//${window.location.hostname}`;
  }
  
  // Default to production (use current page protocol)
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${PRODUCTION_DOMAIN}`;
};

/**
 * Get the full API base URL with /api suffix
 * Use this for direct fetch calls that need the /api prefix
 */
export const getApiUrl = (): string => {
  return `${getApiBaseUrl()}/api`;
};

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private baseURL: string;
  private cacheBuster: number = Date.now(); // Track cache invalidation timestamp

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Invalidate cache - call this on login or when data needs to be refreshed
   */
  invalidateCache(): void {
    this.cacheBuster = Date.now();
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useCache: boolean = false // Allow selective caching
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();
    const isPublicEndpoint = endpoint.startsWith('/api/public/');
    
    // Add cache-busting parameter for non-cached requests
    const separator = url.includes('?') ? '&' : '?';
    const cacheParam = useCache ? '' : `_t=${this.cacheBuster}`;
    const finalUrl = useCache ? url : `${url}${separator}${cacheParam}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        // Prevent browser caching for dashboard data
        'Cache-Control': useCache ? 'max-age=300' : 'no-cache, no-store, must-revalidate',
        'Pragma': useCache ? '' : 'no-cache',
        'Expires': useCache ? '' : '0',
        ...options.headers,
      },
    };

    const response = await fetch(finalUrl, { ...defaultOptions, ...options });
    
    // Handle unauthorized access (401 or 403)
    if (response.status === 401 || response.status === 403) {
      // For public endpoints, don't throw error - allow the response to be processed
      // Public endpoints should not return 401, but if they do, we'll handle it gracefully
      if (isPublicEndpoint) {
        // Try to parse the response anyway - it might be a valid error response
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Unauthorized access.');
        } catch (parseError) {
          throw new Error('Unauthorized access.');
        }
      }
      
      // Only redirect if:
      // 1. It's NOT a public endpoint (public endpoints shouldn't require auth)
      // 2. There was a token (meaning user was trying to authenticate)
      // 3. We're not already on the login page
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/ramp-me-public';
      
      if (token && !isOnLoginPage) {
        // Clear authentication state
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        
        // Redirect to login page only if not already there
        window.location.href = '/login';
        
        throw new Error('Unauthorized access. Please login again.');
      }
      
      // For non-public endpoints when no token, just throw error without redirecting
      throw new Error('Unauthorized access.');
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Download a CSV file from an authenticated endpoint.
   * Uses the same JWT token as other API calls.
   */
  async downloadCsv(endpoint: string): Promise<{ blob: Blob; filename: string }> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        'Accept': 'text/csv',
      },
    });

    // Handle unauthorized access (401 or 403) consistently
    if (response.status === 401 || response.status === 403) {
      const token = this.getAuthToken();
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname === '/ramp-me-public';
      
      // Only redirect if there was a token and we're not on login/public page
      if (token && !isOnLoginPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Unauthorized access. Please login again.');
      }
      
      throw new Error('Unauthorized access.');
    }

    if (!response.ok) {
      throw new Error(`Failed to download CSV (HTTP ${response.status})`);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const filename = match?.[1] || 'report.csv';

    return { blob, filename };
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.request('/api/health');
  }

  /**
   * Get all survey responses with optional filters
   * This is the PRIMARY endpoint - returns raw data for frontend to process
   * 
   * Query Parameters:
   * - district: Comma-separated list of districts
   * - implementationPartnerId: Comma-separated list of partner IDs
   * - workshopId: Comma-separated list of workshop IDs
   * - startDate: Start date (ISO format)
   * - endDate: End date (ISO format)
   * 
   * Returns: Array of SurveyData objects
   * 
   * Note: Frontend filters and calculates all analytics client-side from this data
   */
  async getSurveyResponses(filters: ApiFilterOptions = {}): Promise<SurveyData[]> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const endpoint = `/api/survey/responses${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.request<SurveyData[]>(endpoint);
    
    // Return the data array directly
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  /**
   * Logout - invalidates the current token on the server
   * This should be called before clearing local storage
   */
  async logout(): Promise<ApiResponse> {
    try {
      return await this.request('/api/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      // Even if logout fails, we should still clear local storage
      console.error('Logout API call failed:', error);
      // Return a success response so frontend can proceed with cleanup
      return {
        success: true,
        message: 'Logout processed (API call may have failed)',
        timestamp: new Date().toISOString()
      };
    }
  }

  async getEnrollmentsCount(filters: ApiFilterOptions = {}): Promise<number> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const endpoint = `/api/survey/enrollments/count${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.request<{ enrolled: number }>(endpoint);
    return response.data?.enrolled ?? 0;
  }

  /**
   * Get enrollments counts grouped by implementationPartnerId.
   * Used for "Number of Participants" in Partner/Department detailed tables.
   */
  async getEnrollmentsCountByPartner(filters: ApiFilterOptions = {}): Promise<Array<{ implementationPartnerId: string; enrolled: number }>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const endpoint = `/api/survey/enrollments/count-by-partner${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.request<Array<{ implementationPartnerId: string; enrolled: number }>>(endpoint);
    return Array.isArray(response.data) ? response.data : [];
  }

  // ============================================================================
  // Dashboard metrics (computed server-side)
  // ============================================================================

  private toQueryParams(filters: ApiFilterOptions = {}): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value.toString());
    });
    return params.toString();
  }

  async getFeedbackDashboardMetrics(filters: ApiFilterOptions = {}): Promise<FeedbackDashboardMetrics> {
    const qs = this.toQueryParams(filters);
    const endpoint = `/api/dashboard/feedback${qs ? `?${qs}` : ''}`;
    const response = await this.request<FeedbackDashboardMetrics>(endpoint);
    return response.data as FeedbackDashboardMetrics;
  }

  async getPartnerDashboardMetrics(filters: ApiFilterOptions = {}): Promise<PartnerDashboardMetrics> {
    const qs = this.toQueryParams(filters);
    const endpoint = `/api/dashboard/partners${qs ? `?${qs}` : ''}`;
    const response = await this.request<PartnerDashboardMetrics>(endpoint);
    return response.data as PartnerDashboardMetrics;
  }

  async getDepartmentDashboardMetrics(filters: ApiFilterOptions = {}): Promise<DepartmentDashboardMetrics> {
    const qs = this.toQueryParams(filters);
    const endpoint = `/api/dashboard/departments${qs ? `?${qs}` : ''}`;
    const response = await this.request<DepartmentDashboardMetrics>(endpoint);
    return response.data as DepartmentDashboardMetrics;
  }

  async getMEDashboardMetrics(filters: ApiFilterOptions = {}): Promise<MEDashboardMetrics> {
    const qs = this.toQueryParams(filters);
    const endpoint = `/api/dashboard/me${qs ? `?${qs}` : ''}`;
    const response = await this.request<MEDashboardMetrics>(endpoint);
    return response.data as MEDashboardMetrics;
  }

  async getPublicMEDashboardMetrics(filters: ApiFilterOptions = {}): Promise<PublicMEDashboardMetrics> {
    const qs = this.toQueryParams(filters);
    const endpoint = `/api/public/dashboard/me${qs ? `?${qs}` : ''}`;
    const response = await this.request<PublicMEDashboardMetrics>(endpoint);
    return response.data as PublicMEDashboardMetrics;
  }

  // ============================================================================
  // Implementation Partner Settings (DB-backed)
  // ============================================================================

  async listImplementationPartners(): Promise<Array<{ partnerId: string; partnerName: string; isAttendanceCheckEnabled: boolean }>> {
    const response = await this.request<Array<{ partnerId: string; partnerName: string; isAttendanceCheckEnabled: boolean }>>(
      '/api/implementation-partners'
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  async updateAttendanceCheckSettings(
    updates: Array<{ partnerId: string; isAttendanceCheckEnabled: boolean }>
  ): Promise<{ updated: number }> {
    const response = await this.request<{ updated: number }>('/api/implementation-partners/attendance-check', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });
    return response.data ?? { updated: 0 };
  }

  // ============================================================================
  // Partners and Departments (by type from database)
  // ============================================================================

  async getPartners(): Promise<Array<{ id: string; name: string; partnerType: string; isAttendanceCheckEnabled: boolean }>> {
    const response = await this.request<Array<{ id: string; name: string; partnerType: string; isAttendanceCheckEnabled: boolean }>>(
      '/api/partners'
    );
    return Array.isArray(response.data) ? response.data : [];
  }

  async getDepartments(): Promise<Array<{ id: string; name: string; partnerType: string; isAttendanceCheckEnabled: boolean }>> {
    const response = await this.request<Array<{ id: string; name: string; partnerType: string; isAttendanceCheckEnabled: boolean }>>(
      '/api/departments'
    );
    return Array.isArray(response.data) ? response.data : [];
  }
}

export const apiClient = new ApiClient();
export default apiClient;
